import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  alpha,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Public as PublicIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  EmojiEvents as TrophyIcon,
  BarChart as ChartIcon,
  Error as ErrorIcon,
  People as PeopleIcon,
  CorporateFare as InstitutionIcon,
  Analytics as AnalyticsIcon,
  Policy as PolicyIcon,
  TrendingFlat as ConnectionIcon,
  Security as SecurityIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

function PremierCongresEtudiantsStatistique() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const primaryColor = '#E30613'; // Rouge INSBU
  const secondaryColor = '#008D36'; // Vert INSBU
  const tertiaryColor = '#f5f5f5';
  const white = '#ffffff';
  const textSecondary = '#666666';
  const accentColor = '#008D36'; // Vert INSBU

  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Configuration du chemin d'images
  const getImagePath = (filename) => {
    if (!filename) return '';
    if (filename.startsWith('http')) return filename;
    return `/assets/msb/${filename}`;
  };

  // Fonction de secours pour les images
  const getFallbackImage = (type = 'event') => {
    const fallbacks = {
      conference: "https://images.unsplash.com/photo-1545235617-9465d2a55698?w=800&auto=format&fit=crop",
      portrait: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&auto=format&fit=crop",
      panel: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&auto=format&fit=crop",
      audience: "https://images.unsplash.com/photo-1545235617-9465d2a55698?w=800&auto=format&fit=crop",
      students: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&auto=format&fit=crop"
    };
    return fallbacks[type] || fallbacks.conference;
  };

  // Données principales de l'événement - AJOUT DE RÉFÉRENCES AU MOIS DE LA STATISTIQUE
  const eventData = {
    date: "6-7 Novembre 2025",
    title: "1ER CONGRÈS DES ÉTUDIANTS EN STATISTIQUE AU BURUNDI",
    theme: "Statistiques fiables, outil incontournable dans l'atteinte de la Vision Burundi 2040-2060",
    sousTheme: "Des données aux actions : Le rôle des résultats du RGPHAE dans l'élaboration des politiques et programmes de développement",
    lieu: "Salle des Conférences, Université du Lac Tanganyika",
    contexte: "Dans le cadre du 14ème Mois de la Statistique au Burundi & 10ème Anniversaire du Club Statistique de l'ULT",
    organisateurs: ["Club Statistique de l'Université du Lac Tanganyika"],
    partenaires: [
      "Association des Professionnels de la Statistique au Burundi (APROSTAB)",
      "Université du Lac Tanganyika (ULT)",
      "Institut National de la Statistique du Burundi (INSBU)",
      "Fonds des Nations Unies pour la Population (UNFPA)"
    ],
    objectifs: [
      "Renforcer les liens entre institutions académiques et professionnelles",
      "Favoriser l'échange d'expériences entre jeunes statisticiens",
      "Promouvoir l'utilisation des données statistiques dans la prise de décision",
      "Célébrer 10 ans d'engagement du Club Statistique",
      "Contribuer aux activités du 14ème Mois de la Statistique au Burundi"
    ]
  };

  // Chiffres clés révélés pendant le congrès
  const chiffresCles = [
    {
      chiffre: "12 332 788",
      description: "Habitants au Burundi selon les résultats préliminaires du RGPHAE 2022",
      icon: <PeopleIcon />,
      color: primaryColor
    },
    {
      chiffre: "1/2",
      description: "Un Burundais sur deux a moins de 17 ans",
      icon: <PersonIcon />,
      color: secondaryColor
    },
    {
      chiffre: "2/3",
      description: "Deux tiers de la population ont moins de 25 ans",
      icon: <GroupsIcon />,
      color: accentColor
    },
    {
      chiffre: "500/km²",
      description: "Densité de population, faisant du Burundi l'un des pays les plus densément peuplés",
      icon: <PublicIcon />,
      color: '#ff9800'
    }
  ];

  // Intervenants principaux
  const intervenants = [
    {
      nom: "M. Damas NDAYIKENGURUKIYE",
      titre: "Président du Club Statistique de l'ULT",
      role: "Discours de bienvenue et présentation des objectifs du congrès",
      photo: null,
      description: "A rappelé le 10ème anniversaire du Club Statistique et présenté les résultats attendus du congrès dans le cadre du Mois de la Statistique",
      ordre: 1,
      institution: "Université du Lac Tanganyika"
    },
    {
      nom: "M. Désiré MUNEZERO",
      titre: "Président de l'APROSTAB",
      role: "Intervention en tant que partenaire du congrès",
      photo: null,
      description: "A salué l'engagement des jeunes statisticiens et expliqué les motivations de l'APROSTAB à soutenir cette initiative du Mois de la Statistique",
      ordre: 2,
      institution: "APROSTAB"
    },
    {
      nom: "Prof. François Xavier MUREHA",
      titre: "Recteur de l'Université du Lac Tanganyika",
      role: "Allocution d'ouverture officielle",
      photo: null,
      description: "A félicité le Club Statistique et souligné son rôle dans la formation professionnelle des étudiants, en marge des célébrations du Mois de la Statistique",
      ordre: 3,
      institution: "Université du Lac Tanganyika"
    },
    {
      nom: "M. Nicolas NDAYISHIMIYE",
      titre: "Directeur Général de l'INSBU & Président du BCR",
      role: "Discours de clôture et intervention lors du panel",
      photo: null,
      description: "A encouragé les étudiants et présenté l'importance stratégique des données du RGPHAE, soulignant l'importance de ce congrès dans le cadre du Mois de la Statistique",
      ordre: 4,
      institution: "INSBU/BCR"
    },
    {
      nom: "Dr Salomon NSABIMANA",
      titre: "Enseignant-Chercheur à l'Université du Burundi",
      role: "Expert paneliste",
      photo: null,
      description: "A mis en avant l'importance des données fiables pour la planification des politiques publiques, dans le contexte du Mois de la Statistique",
      ordre: 5,
      institution: "Université du Burundi"
    },
    {
      nom: "M. Judicaël ELIDJE",
      titre: "Représentant Résident de l'UNFPA au Burundi",
      role: "Expert paneliste et partenaire technique",
      photo: null,
      description: "A partagé des statistiques clés et encouragé les étudiants à exploiter les données, contribuant ainsi aux objectifs du Mois de la Statistique",
      ordre: 6,
      institution: "UNFPA"
    }
  ];

  // Thèmes des présentations du 2ème jour
  const themesPresentations = [
    {
      titre: "Budget-Programme",
      description: "L'apport des départements statistiques et du suivi-évaluation à l'efficacité de la politique budgétaire",
      icon: <AssessmentIcon />,
      points: [
        "Rôles des départements statistiques",
        "Défis rencontrés",
        "Stratégies d'amélioration"
      ]
    },
    {
      titre: "Affiliation Internationale",
      description: "Opportunités pour YASA et Takwimu dans les organisations régionales et continentales",
      icon: <ConnectionIcon />,
      points: [
        "Organisations statistiques africaines",
        "Avantages de l'affiliation",
        "Perspectives de collaboration"
      ]
    },
    {
      titre: "Éthique et Protection",
      description: "Les défis de la statistique à l'ère numérique",
      icon: <SecurityIcon />,
      points: [
        "Protection des données personnelles",
        "Enjeux éthiques",
        "Cadres réglementaires"
      ]
    }
  ];

  // Galerie photos
  const photosEvenement = [
    {
      id: 1,
      src: "CONGRES1.jpeg",
      alt: "Panel des experts lors du 1er Congrès des Étudiants en Statistique",
      titre: "Panel des Experts",
      description: "Échanges autour du thème 'Des données aux actions' dans le cadre du Mois de la Statistique",
      category: "panel",
      objectFit: 'cover',
      containerHeight: 300
    },
    {
      id: 2,
      src: "CONGRES2.jpeg",
      alt: "Public d'étudiants attentifs pendant les présentations",
      titre: "Audience Étudiante",
      description: "Étudiants participant activement au congrès du Mois de la Statistique",
      category: "audience",
      objectFit: 'cover',
      containerHeight: 300
    },
    {
      id: 3,
      src: "CONGRES3.jpeg",
      alt: "Vue générale de la salle de conférence pendant le congrès",
      titre: "Salle des Conférences",
      description: "Cadre académique du 1er Congrès des Étudiants en Statistique organisé pendant le Mois de la Statistique",
      category: "conference",
      objectFit: 'cover',
      containerHeight: 300
    }
  ];

  // Mécanismes de suivi présentés
  const mecanismesSuivi = [
    "Comités techniques multisectoriels",
    "Indicateurs de performance",
    "Missions de supervision sur le terrain",
    "Rapports périodiques d'avancement",
    "Assurance qualité des données",
    "Validation technique des résultats"
  ];

  // Composant Image Optimisé
  const OptimizedImage = ({ 
    src, 
    alt, 
    height = 300, 
    width = '100%', 
    priority = false, 
    onClick, 
    showPreview = true,
    objectFit = 'cover',
    isAvatar = false,
    fallbackType = 'conference'
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
      const url = getImagePath(src);
      setImageUrl(url);
      
      if (priority && url) {
        const img = new Image();
        img.src = url;
        img.onload = () => setIsLoading(false);
        img.onerror = () => {
          setHasError(true);
          setIsLoading(false);
          setImageUrl(getFallbackImage(fallbackType));
        };
      }
    }, [src, priority, fallbackType]);

    const handleImageLoad = () => setIsLoading(false);
    const handleImageError = () => {
      setHasError(true);
      setIsLoading(false);
      setImageUrl(getFallbackImage(fallbackType));
    };

    const handleClick = () => {
      if (onClick && !hasError && !isLoading) onClick();
    };

    const optimalObjectFit = isAvatar ? 'contain' : objectFit;

    if (hasError && !imageUrl.includes('unsplash')) {
      return (
        <Box sx={{ 
          height, width, 
          bgcolor: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          border: '1px dashed #dee2e6'
        }}>
          <ErrorIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 1.5 }} />
          <Typography variant="caption" sx={{ color: '#6c757d', textAlign: 'center', px: 2 }}>
            Image non disponible
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        height, width,
        overflow: 'hidden',
        bgcolor: isLoading ? '#f8f9fa' : 'transparent',
        cursor: (showPreview && !isLoading) ? 'pointer' : 'default',
        borderRadius: 1,
        transition: 'transform 0.3s ease',
        '&:hover': showPreview && !isLoading ? { transform: 'scale(1.02)' } : {}
      }} onClick={handleClick}>
        {isLoading && (
          <Skeleton variant="rectangular" width="100%" height={height}
            sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, borderRadius: 1 }} />
        )}
        
        <Box component="img" src={imageUrl || getFallbackImage(fallbackType)} alt={alt}
          onLoad={handleImageLoad} onError={handleImageError}
          sx={{
            width: '100%', height: '100%',
            objectFit: optimalObjectFit,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            display: 'block',
            borderRadius: 1
          }} loading={priority ? "eager" : "lazy"} />
        
        {showPreview && !isLoading && !hasError && !isAvatar && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', opacity: 0, transition: 'opacity 0.3s ease',
            '&:hover': { opacity: 1 }
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.95)', borderRadius: '50%', p: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <ZoomInIcon sx={{ color: primaryColor, fontSize: 28 }} />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Effet de chargement
  useEffect(() => {
    setTimeout(() => setLoading(false), 500);
  }, []);

  // Fonctions utilitaires
  const handleImageClick = (photo) => {
    setSelectedImage(photo);
    setZoomOpen(true);
  };

  const handleNavigation = (path) => navigate(path);
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: eventData.title,
        text: eventData.theme,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier !');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', 
        justifyContent: 'center', bgcolor: tertiaryColor, flexDirection: 'column', gap: 3 }}>
        <CircularProgress sx={{ color: primaryColor, width: '60px !important', height: '60px !important' }} />
        <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 'bold' }}>
          Chargement de l'article...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: tertiaryColor,
      background: `linear-gradient(135deg, ${tertiaryColor} 0%, ${alpha(accentColor, 0.05)} 100%)`
    }}>
      <NavigationBar />
      
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
        {/* Navigation */}
        <Fade in timeout={500}>
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Button onClick={() => handleNavigation('/actualitesEtEvenements')}
                startIcon={<ArrowBackIcon />} variant="outlined"
                sx={{ borderColor: primaryColor, color: primaryColor, fontWeight: 'bold',
                  '&:hover': { bgcolor: alpha(primaryColor, 0.1), borderColor: primaryColor } }}>
                Retour aux actualités
              </Button>
              <IconButton onClick={handleShare}
                sx={{ color: secondaryColor, bgcolor: alpha(secondaryColor, 0.1),
                  '&:hover': { bgcolor: alpha(secondaryColor, 0.2) } }}>
                <ShareIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ borderColor: alpha('#000', 0.1), borderWidth: 1 }} />
          </Box>
        </Fade>

        {/* En-tête Principal - AJOUT DE RÉFÉRENCE AU MOIS DE LA STATISTIQUE */}
        <Fade in timeout={800}>
          <Paper elevation={0} sx={{ 
            p: { xs: 3, sm: 4 }, mb: 4, bgcolor: white,
            background: `linear-gradient(135deg, ${white} 0%, ${alpha(accentColor, 0.03)} 100%)`,
            borderLeft: `6px solid ${accentColor}`, borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)', position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute', top: 0, right: 0,
              width: '150px', height: '150px',
              background: `radial-gradient(circle, ${alpha(secondaryColor, 0.1)} 0%, transparent 70%)`,
              transform: 'translate(50%, -50%)'
            }
          }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <Chip icon={<CalendarIcon />} label={eventData.date}
                sx={{ bgcolor: accentColor, color: 'white', fontWeight: 'bold',
                  fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(0, 141, 54, 0.2)' }} />
              <Chip icon={<CampaignIcon />} label="Activité du 14ème Mois de la Statistique"
                sx={{ bgcolor: primaryColor, color: 'white', fontWeight: 'bold',
                  fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(227, 6, 19, 0.2)' }} />
            </Stack>
            
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom
              sx={{ fontWeight: 800, color: accentColor, mb: 2, lineHeight: 1.3,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              {eventData.title}
            </Typography>
            
            <Box sx={{ bgcolor: alpha(secondaryColor, 0.1), p: 3, borderRadius: 2, mb: 3,
              borderLeft: `4px solid ${secondaryColor}` }}>
              <Typography variant="h6" sx={{ color: secondaryColor, fontWeight: 700,
                fontStyle: 'italic', lineHeight: 1.4 }}>
                {eventData.theme}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" sx={{ color: textSecondary, mb: 3,
              fontStyle: 'italic', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {eventData.sousTheme}
            </Typography>
            
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, gap: 1 }}>
              <Chip icon={<EventIcon />} label={eventData.contexte} size="small"
                sx={{ borderColor: accentColor, color: accentColor, fontWeight: 'bold',
                  bgcolor: alpha(accentColor, 0.05) }} variant="outlined" />
              <Chip icon={<SchoolIcon />} label={eventData.lieu} size="small"
                sx={{ borderColor: secondaryColor, color: secondaryColor, fontWeight: 'bold',
                  bgcolor: alpha(secondaryColor, 0.05) }} variant="outlined" />
            </Stack>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <GroupsIcon sx={{ mr: 1, color: accentColor, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: textSecondary }}>
                    Organisateurs:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {eventData.organisateurs.map((org, index) => (
                    <Chip key={index} label={org} size="small" variant="outlined"
                      sx={{ borderColor: accentColor, color: accentColor, fontWeight: 'bold' }} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InstitutionIcon sx={{ mr: 1, color: secondaryColor, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: textSecondary }}>
                    Partenaires:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {eventData.partenaires.map((part, index) => (
                    <Chip key={index} label={part} size="small"
                      sx={{ bgcolor: alpha(secondaryColor, 0.1), color: secondaryColor,
                        fontWeight: 'bold' }} />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Section spéciale Mois de la Statistique */}
        <Grow in timeout={900}>
          <Paper sx={{ 
            p: 4, 
            mb: 6, 
            bgcolor: alpha(primaryColor, 0.08),
            borderRadius: 3,
            border: `2px solid ${alpha(primaryColor, 0.3)}`,
            boxShadow: '0 4px 20px rgba(227, 6, 19, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CampaignIcon sx={{ fontSize: 32, color: primaryColor, mr: 2 }} />
              <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 'bold' }}>
                Dans le cadre du 14ème Mois de la Statistique au Burundi
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: textSecondary, mb: 2 }}>
              Ce 1er Congrès des Étudiants en Statistique s'inscrit dans la programmation officielle 
              du <strong>14ème Mois de la Statistique au Burundi (MSB)</strong>, une initiative annuelle 
              visant à promouvoir la culture statistique et renforcer les capacités des acteurs du secteur.
            </Typography>
            
            <Typography variant="body1" sx={{ lineHeight: 1.8, color: textSecondary }}>
              Organisé en novembre 2025, il contribue directement aux objectifs du MSB en matière de :
              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                <li><Typography variant="body2" sx={{ color: textSecondary }}>Sensibilisation des jeunes à l'importance des statistiques</Typography></li>
                <li><Typography variant="body2" sx={{ color: textSecondary }}>Renforcement des liens entre monde académique et professionnel</Typography></li>
                <li><Typography variant="body2" sx={{ color: textSecondary }}>Promotion de l'utilisation des données pour le développement</Typography></li>
                <li><Typography variant="body2" sx={{ color: textSecondary }}>Valorisation de la relève statistique burundaise</Typography></li>
              </Box>
            </Typography>
          </Paper>
        </Grow>

        {/* Introduction et Contexte - AJOUT DE RÉFÉRENCE AU MOIS DE LA STATISTIQUE */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Grow in timeout={1000}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: white, borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h5" gutterBottom sx={{ color: accentColor, fontWeight: 'bold', mb: 3 }}>
                  <SchoolIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                  Contexte Historique
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3, color: textSecondary }}>
                  <strong>S'inscrivant dans le cadre du 14ème Mois de la Statistique au Burundi</strong>, 
                  et sous le thème central « Statistiques fiables, outil incontournable dans l'atteinte 
                  de la Vision Burundi 2040-2060 », le Club Statistique de l'Université du Lac Tanganyika, 
                  en collaboration avec l'APROSTAB, l'ULT et l'INSBU, a organisé du 6 au 7 novembre 2025 
                  le premier congrès des étudiants en statistique au Burundi.
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3, color: textSecondary }}>
                  Cet événement historique, <strong>organisé pendant le Mois de la Statistique</strong>, 
                  s'inscrivait également dans le cadre de la célébration du 10ᵉ anniversaire du Club 
                  Statistique de l'ULT, marquant une décennie d'engagement pour la promotion de la 
                  statistique et la professionnalisation des étudiants.
                </Typography>
                
                <Box sx={{ p: 3, bgcolor: alpha(accentColor, 0.05), borderRadius: 2, mt: 3 }}>
                  <Typography variant="h6" sx={{ color: accentColor, fontWeight: 'bold', mb: 2 }}>
                    Objectifs du Congrès dans le cadre du MSB
                  </Typography>
                  <List disablePadding>
                    {eventData.objectifs.map((objectif, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon sx={{ color: secondaryColor }} />
                        </ListItemIcon>
                        <ListItemText primary={objectif} 
                          primaryTypographyProps={{ color: textSecondary }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Paper>
            </Grow>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Grow in timeout={1200}>
              <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden',
                borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: `1px solid ${alpha('#000', 0.1)}` }}>
                <OptimizedImage src={photosEvenement[2].src} alt={photosEvenement[2].alt}
                  height={400} priority={true} objectFit={photosEvenement[2].objectFit}
                  onClick={() => handleImageClick(photosEvenement[2])}
                  fallbackType={photosEvenement[2].category} />
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                  bgcolor: 'rgba(0,0,0,0.75)', p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 'medium' }}>
                    Salle des Conférences de l'Université du Lac Tanganyika pendant le Mois de la Statistique
                  </Typography>
                </Box>
              </Box>
            </Grow>
          </Grid>
        </Grid>

        {/* Chiffres Clés Révélés */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold', mb: 4,
            textAlign: 'center', position: 'relative', '&::after': {
              content: '""', position: 'absolute', bottom: -10, left: '50%',
              transform: 'translateX(-50%)', width: '100px', height: '3px',
              bgcolor: primaryColor, borderRadius: 2
            } }}>
            <AnalyticsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Chiffres Clés du RGPHAE révélés pendant le MSB
          </Typography>

          <Grid container spacing={3}>
            {chiffresCles.map((chiffre, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={500 + (index * 200)}>
                  <Card sx={{ height: '100%', transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-6px)' },
                    borderTop: `4px solid ${chiffre.color}` }}>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box sx={{ display: 'inline-flex', p: 2, mb: 2,
                        bgcolor: alpha(chiffre.color, 0.1), borderRadius: '50%' }}>
                        {React.cloneElement(chiffre.icon, { sx: { color: chiffre.color, fontSize: 32 } })}
                      </Box>
                      <Typography variant="h3" sx={{ color: chiffre.color, fontWeight: 'bold', mb: 1 }}>
                        {chiffre.chiffre}
                      </Typography>
                      <Typography variant="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                        {chiffre.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Cérémonie d'Ouverture */}
        <Paper sx={{ p: 4, mb: 2, bgcolor: white, borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderLeft: `6px solid ${accentColor}` }}>
          <Typography variant="h5" gutterBottom sx={{ color: accentColor, fontWeight: 'bold', mb: 4 }}>
            <MicIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Cérémonie d'Ouverture du Congrès du MSB
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {intervenants.slice(0, 4).map((intervenant, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                  <Avatar sx={{ width: 80, height: 80, mb: 2, mx: 'auto',
                    bgcolor: alpha(accentColor, 0.1), color: accentColor,
                    border: `3px solid ${accentColor}` }}>
                    <PersonIcon fontSize="large" />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: accentColor, mb: 1 }}>
                    {intervenant.nom}
                  </Typography>
                  <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 'bold', mb: 1, display: 'block' }}>
                    {intervenant.titre}
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary, fontStyle: 'italic' }}>
                    {intervenant.role}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: textSecondary }}>
            Dans son discours de bienvenue, <strong>M. Damas NDAYIKENGURUKIYE</strong>, président du Club 
            Statistique, a présenté les grands objectifs du congrès inscrit dans le Mois de la Statistique 2025
            et les résultats attendus, notamment le renforcement des liens entre les institutions et l'échange 
            d'expériences entre les jeunes statisticiens du Burundi.
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: textSecondary }}>
            <strong>Prof. François Xavier MUREHA</strong>, Recteur de l'ULT, a souligné que le Club 
            Statistique n'est pas un club ordinaire, mais un cadre de formation et d'insertion 
            professionnelle, qui permet aux étudiants d'acquérir des compétences pratiques à travers 
            des formations, conférences et projets innovants.
          </Typography>
          
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: textSecondary }}>
            Clôturant la cérémonie, <strong>M. Nicolas NDAYISHIMIYE</strong>, Directeur Général de 
            l'INSBU, a exprimé sa satisfaction et sa fierté de voir la jeunesse burundaise s'investir 
            activement dans la promotion de la statistique, réaffirmant le soutien de l'INSBU à ce type 
            d'initiatives dans le cadre du Mois de la Statistique 2025.
          </Typography>
        </Paper>

        {/* Panel-Débat */}
        <Grid container spacing={1} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Grow in timeout={1000}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: white, borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 'bold', mb: 3 }}>
                  <GroupsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                  Panel-Débat : Des données aux actions
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3, color: textSecondary }}>
                  <strong>Point d'orgue du Mois de la Statistique</strong>, le panel du 6 novembre 2025 a réuni 
                  des experts de haut niveau autour du thème « Des données aux actions : Le rôle des résultats 
                  du RGPHAE dans l'élaboration des politiques et programmes de développement ».
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 2 }}>
                    Intervention du Directeur Général de l'INSBU et Président du BCR
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: textSecondary }}>
                    <strong>M. Nicolas NDAYISHIMIYE</strong> a introduit le débat en soulignant 
                    l'importance stratégique des données issues du recensement dans la planification 
                    du développement national, notamment en lien avec les priorités définies dans 
                    la Vision Burundi 2040–2060, <strong>thématique centrale du Mois de la Statistique 2025</strong>.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 2 }}>
                    Mécanismes de Suivi et Évaluation
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: textSecondary, mb: 2 }}>
                    Concernant le suivi et l'évaluation du recensement, il a précisé que plusieurs 
                    outils ont été mobilisés pour garantir la qualité, la transparence et l'utilisation 
                    efficace des données :
                  </Typography>
                  <Grid container spacing={1}>
                    {mecanismesSuivi.map((mecanisme, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: secondaryColor, mr: 1 }} />
                          <Typography variant="body2" sx={{ color: textSecondary }}>
                            {mecanisme}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Grow>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Grow in timeout={1200}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper sx={{ p: 4, bgcolor: alpha(secondaryColor, 0.05), borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 2 }}>
                    <PolicyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Perspective Académique
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: textSecondary }}>
                    <strong>Dr Salomon NSABIMANA</strong> a mis en avant l'importance de disposer de 
                    données fiables issues du RGPHAE au niveau national. Selon lui, ces données sont 
                    essentielles non seulement pour la planification des politiques publiques, mais 
                    aussi pour leur suivi et évaluation.
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 4, bgcolor: alpha(accentColor, 0.05), borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: accentColor, fontWeight: 'bold', mb: 2 }}>
                    <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Perspective  Partenaires
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: textSecondary }}>
                    <strong>M. Judicaël ELIDJE</strong> de l'UNFPA a partagé des statistiques clés 
                    qui redéfinissent les priorités du pays et encouragé les étudiants à consulter 
                    les rapports du BCR et à intégrer davantage l'analyse de données dans leurs recherches, 
                    <strong> contribuant ainsi aux objectifs pédagogiques du Mois de la Statistique</strong>.
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 2, borderLeft: `4px solid ${accentColor}` }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: textSecondary }}>
                      "Les données sont là. À nous de les transformer en décisions éclairées."
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grow>
          </Grid>
        </Grid>

        {/* Deuxième Jour : Présentations */}
        <Paper sx={{ p: 4, mb: 8, bgcolor: white, borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderTop: `4px solid ${secondaryColor}` }}>
          <Typography variant="h5" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 4 }}>
            <CalendarIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Deuxième Jour : Présentations Techniques du MSB
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 4, color: textSecondary }}>
            <strong>Dans la continuité des activités du Mois de la Statistique</strong>, le deuxième jour du congrès 
            a été marqué par une série de présentations techniques couvrant des domaines essentiels 
            de la statistique moderne :
          </Typography>
          
          <Grid container spacing={1}>
            {themesPresentations.map((theme, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' },
                  border: `1px solid ${alpha('#000', 0.1)}` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'inline-flex', p: 1.5, mb: 2,
                      bgcolor: alpha(accentColor, 0.1), borderRadius: 2 }}>
                      {React.cloneElement(theme.icon, { sx: { color: accentColor, fontSize: 24 } })}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ color: accentColor, fontWeight: 'bold' }}>
                      {theme.titre}
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ color: textSecondary, mb: 2 }}>
                      {theme.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ color: primaryColor, fontWeight: 'bold', mb: 1 }}>
                      Points abordés :
                    </Typography>
                    <List dense disablePadding>
                      {theme.points.map((point, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <TrendingIcon sx={{ fontSize: 16, color: secondaryColor }} />
                          </ListItemIcon>
                          <ListItemText primary={point} 
                            primaryTypographyProps={{ variant: 'body2', color: textSecondary }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Galerie Photos */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: primaryColor, fontWeight: 'bold', mb: 4,
            textAlign: 'center', position: 'relative', '&::after': {
              content: '""', position: 'absolute', bottom: -10, left: '50%',
              transform: 'translateX(-50%)', width: '100px', height: '3px',
              bgcolor: secondaryColor, borderRadius: 2
            } }}>
            <PhotoCameraIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Galerie du Congrès du Mois de la Statistique
          </Typography>

          <Grid container spacing={3}>
            {photosEvenement.map((photo, index) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Grow in timeout={300 + (index * 200)}>
                  <Card sx={{ height: '100%', transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-6px)' },
                    overflow: 'hidden', border: `1px solid ${alpha('#000', 0.1)}` }}>
                    <Box sx={{ position: 'relative', height: photo.containerHeight || 300 }}>
                      <OptimizedImage src={photo.src} alt={photo.alt}
                        height={photo.containerHeight || 300} priority={index < 1}
                        onClick={() => handleImageClick(photo)}
                        objectFit={photo.objectFit} fallbackType={photo.category} />
                      <Box sx={{ position: 'absolute', top: 10, right: 10,
                        bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '50%',
                        width: 32, height: 32, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <PhotoCameraIcon sx={{ fontSize: 16, color: primaryColor }} />
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: primaryColor }}>
                        {photo.titre}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                        {photo.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Conclusion et Perspectives */}
        <Paper sx={{ p: 4, mb: 8, bgcolor: white, borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          background: `linear-gradient(135deg, ${white} 0%, ${alpha(secondaryColor, 0.03)} 100%)` }}>
          <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 'bold', mb: 3 }}>
            <TrophyIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Conclusion et Perspectives
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3, color: textSecondary }}>
            <strong>Le 1er Congrès des Étudiants en Statistique au Burundi, organisé pendant le Mois de la Statistique,</strong> 
            a constitué un jalon historique dans la promotion de la culture statistique chez les jeunes. Il a démontré 
            la vitalité de la relève statistique burundaise et son engagement pour contribuer au développement 
            national à travers des données fiables, <strong>concrétisant ainsi les objectifs du 14ème Mois de la Statistique</strong>.
          </Typography>
          
          <Grid container spacing={1} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, bgcolor: alpha(secondaryColor, 0.05), borderRadius: 2,
                height: '100%', border: `1px solid ${alpha(secondaryColor, 0.2)}` }}>
                <Typography variant="h6" sx={{ color: secondaryColor, fontWeight: 'bold', mb: 2 }}>
                  Réalisations dans le cadre du MSB
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: secondaryColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Renforcement des liens institutions-étudiants" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: secondaryColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Partage d'expertise entre professionnels et académiques" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: secondaryColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Sensibilisation sur l'importance des données du RGPHAE" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: secondaryColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Contribution aux objectifs du Mois de la Statistique 2025" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, bgcolor: alpha(accentColor, 0.05), borderRadius: 2,
                height: '100%', border: `1px solid ${alpha(accentColor, 0.2)}` }}>
                <Typography variant="h6" sx={{ color: accentColor, fontWeight: 'bold', mb: 2 }}>
                  Perspectives pour les prochaines éditions du MSB
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TrendingIcon sx={{ color: accentColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Édition annuelle du congrès intégrée au Mois de la Statistique" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TrendingIcon sx={{ color: accentColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Extension à d'autres universités burundaises pendant le MSB" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TrendingIcon sx={{ color: accentColor }} />
                    </ListItemIcon>
                    <ListItemText primary="Publication des actes du congrès comme contribution au MSB" 
                      primaryTypographyProps={{ color: textSecondary }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Navigation */}
        <Paper sx={{ p: 4, mb: 2, bgcolor: white, borderRadius: 3,
          border: `1px solid ${alpha(accentColor, 0.2)}`, textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" gutterBottom sx={{ color: accentColor, fontWeight: 'bold', mb: 3 }}>
            Explorez d'autres événements du Mois de la Statistique 2025
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button onClick={() => handleNavigation('/cafeStatistiqueRGPH_RGAE')}
              variant="contained" startIcon={<EventIcon />}
              sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: '#c60511' },
                px: 3, py: 1.2, fontWeight: 'bold' }}>
              Café Statistique RGPHAE
            </Button>
            
            <Button onClick={() => handleNavigation('/lancementMoisStatistique2025')}
              variant="contained" startIcon={<CalendarIcon />}
              sx={{ bgcolor: secondaryColor, '&:hover': { bgcolor: '#007a2f' },
                px: 3, py: 1.2, fontWeight: 'bold' }}>
              Lancement MSB 2025
            </Button>
            
            <Button onClick={() => handleNavigation('/actualitesEtEvenements')}
              variant="outlined" startIcon={<ArrowBackIcon />}
              sx={{ borderColor: accentColor, color: accentColor,
                '&:hover': { borderColor: accentColor, bgcolor: alpha(accentColor, 0.1) },
                px: 3, py: 1.2, fontWeight: 'bold' }}>
              Toutes les actualités du MSB
            </Button>
          </Stack>
        </Paper>
         <Paper sx={{ p: 4, mb: 8, bgcolor: white, borderRadius: 3,
          border: `1px solid ${alpha(accentColor, 0.2)}`, textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" gutterBottom sx={{ color: accentColor, fontWeight: 'bold', mb: 3 }}>
           Présenté par Ir. Léandre NIMPAGARITSE, Informaticien – Agent de publication, diffusion et communication à l’INSBU.
          </Typography>
         </Paper>
      </Container>
      
      {/* Modal pour zoom sur image */}
      <Dialog open={zoomOpen} onClose={() => setZoomOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', maxHeight: '90vh' } }}>
        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
          <IconButton onClick={() => setZoomOpen(false)}
            sx={{ position: 'absolute', top: 16, right: 16,
              bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' }, zIndex: 1000 }}>
            <CloseIcon />
          </IconButton>
          
          {selectedImage && (
            <OptimizedImage src={selectedImage.src} alt={selectedImage.alt}
              height="auto" width="100%" showPreview={false} onClick={() => {}}
              objectFit="contain" fallbackType={selectedImage.category} />
          )}
          
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
            bgcolor: 'rgba(0,0,0,0.8)', p: 2, color: 'white' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {selectedImage?.titre}
            </Typography>
            <Typography variant="body2">
              {selectedImage?.description}
            </Typography>
          </Box>
        </Box>
      </Dialog>
      
      <Footer />
    </Box>
  );
}

export default PremierCongresEtudiantsStatistique;